import useSession from "Utils/Hooks/useSession";
import { useEffect, useRef, useState } from "react";
import { FUND_SOURCE_TYPE, PRODUCTTYPES } from "Utils/Constants";
import { faro, LogLevel } from "@grafana/faro-react";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import useView, { VIEW } from "Utils/Hooks/useView";
import { useHistory } from "react-router";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import useFundType, { FUNDTYPE } from "Utils/Hooks/useFundType";
import { findDefaultBank } from "Utils/Utils";
import { useVipAccountCache } from "context/VipAccountContext";

// extracted from existing code in MyAccounts page for reusability
export function useLimitAndProductSwitching() {
  const { session, fetchSession } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const noWarranty = session.noWarranty;
  const fundType = useFundType();
  const view = useView();
  const history = useHistory();
  const fundingSourceList = useRef(null);
  const [limitAmount, setLimitAmount] = useState(session.depositLimit);
  const [preferredPreviousLimit, setPreferredPreviousLimit] = useState(0);
  const [defaultFund, setDefaultFund] = useState();
  const [preferredCurrentLimit, setPreferredCurrentLimit] = useState(0);
  const operatorDailyLimit = session.depositLimit;
  const isCashierPage = view === VIEW.cashier;
  const patronProductType = session?.patronProductType;
  const patronSelectedProductType = session?.productType;
  const isOperatorPreferred = session?.operatorProducts.includes(
    PRODUCTTYPES.PREFERRED,
  );
  const [hasVerifiedBank, setHasVerifiedBanks] = useSessionStorage(
    "has-verified-bank",
    false,
  );
  const [unVerifiedBank, setDeclinedVerifiedBank] = useSessionStorage(
    "unverifiedbank",
    false,
  );
  const isOperatorOnline = session?.operatorProducts.includes(
    PRODUCTTYPES.ONLINE,
  );
  const isOperatorBoth = isOperatorOnline && isOperatorPreferred;
  const [showMessage, setShowMessage] = useState(
    patronProductType !== patronSelectedProductType &&
      isOperatorBoth &&
      !session?.toggles?.productRoutingEnabled,
  );
  const [isRepeat, setIsRepeat] = useSessionStorage("isRepeat", false);
  const mode = useMode();
  const vipApi = useVipApiFetcher();
  const { setVipAccount } = useVipAccountCache();
  const manageFundingSources = mode === MODE.manageBanks;
  const fundingSources = useRef([]);
  const productType =
    session?.patronProductType === PRODUCTTYPES.PREFERRED ? "VIPP" : "VIPO";
  const productRoutingEnabled = session?.toggles?.productRoutingEnabled;
  // No document.title here: this hook is used by components nested inside a page
  // (Finicity sits under FundingSourceList), and a title written during render would
  // overwrite the host page's own. Each page sets its own title.
  const title = "My Funding Sources";

  const showModals =
    !isCashierPage &&
    manageFundingSources &&
    showMessage &&
    !isRepeat &&
    !session.productTypeSwitched &&
    mode !== MODE.manageBanks;

  async function onVipOnlineSubmit(event) {
    if (event) event.preventDefault();
    setShowMessage(false);
    if (
      patronProductType === PRODUCTTYPES.PREFERRED &&
      patronSelectedProductType === PRODUCTTYPES.ONLINE
    ) {
      await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
    }
    setIsRepeat(true);
  }

  async function onVipPreferredSubmit(event) {
    if (event) event.preventDefault();
    setShowMessage(false);
    if (
      patronProductType === PRODUCTTYPES.ONLINE &&
      patronSelectedProductType === PRODUCTTYPES.PREFERRED
    ) {
      await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
      setLimitAmount(Math.min(operatorDailyLimit, preferredCurrentLimit));
    }
    setIsRepeat(true);
  }

  /**
   * Switch the patron's product type and refresh the session to match.
   * The chain is returned so callers awaiting the switch actually wait for it - the
   * product change and the session refetch must complete before anything downstream
   * reads session.patronProductType.
   * @returns {Promise<boolean>} true when this navigated the patron elsewhere.
   */
  async function SubmitProductTypeChange(selectedProductType) {
    setIsLoading(true);
    return vipApi
      .fetch("/api/account/switch", "POST", {
        productType: selectedProductType,
      })
      .then(async (response) => {
        if (response.ok) {
          await fetchSession(true);
          const updatedAccount = await vipApi.fetchData("/api/account");
          if (updatedAccount) {
            setVipAccount(updatedAccount);
          }
          let fundList = fundingSourceList.current;
          if (selectedProductType === PRODUCTTYPES.ONLINE) {
            fundList = await vipApi.fetchData("/api/bank/ach");
          }
          if (isOperatorBoth) {
            if (selectedProductType === PRODUCTTYPES.ONLINE) {
              fundList = fundList.filter(
                (fund) =>
                  (fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                    fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                  fund.availableLimit,
              ); // Get only Plaid accounts
              if (fundList.length > 0) {
                setLimitAmount(
                  Math.min(operatorDailyLimit, fundList[0].availableLimit),
                );
              }
              if (!fundList || fundList.length === 0) {
                history.push({
                  pathname: "/addAccount",
                  state: { notRedirect: true },
                });
                return true;
              }
            }
          } else if (isOperatorOnline) {
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit,
            ); // Get only Plaid accounts
            if (fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit),
              );
            }
            if (!fundList || fundList.length === 0) {
              history.push({
                pathname: "/addAccount",
                state: { notRedirect: true },
              });
              return true;
            }
          }
          fundingSources.current = fundList;
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);
          return false;
        } else {
          faro.api.pushError(new Error("Product Type Switch Error"), {
            context: response,
          });
          if (response.status === 500) {
            console.error(response.statusText);
            history.push({
              pathname: "/error",
            });
          } else if (response.status === 401) {
            console.error(response.statusText);
            history.push({
              pathname: "/expired",
            });
          } else {
            console.error(response.statusText);
            history.push({
              pathname: "/error",
            });
          }
          return true;
        }
      })
      .catch((error) => {
        console.error(error);
        faro.api.pushError(new Error("Product Type Switch Network Error"), {
          context: error,
        });
        history.push({
          pathname: "/error",
        });
        return true;
      });
  }

  /**
   * Re-read the account and funding sources and apply the product routing rules.
   * Some outcomes navigate the patron elsewhere (no eligible verified bank, a failed
   * switch), so report that - a caller that means to navigate afterwards must not push
   * its own destination over one of those.
   * @returns {Promise<boolean>} true when this navigated the patron elsewhere.
   */
  async function determineProductSwitchAndLimits(mounted = true) {
    let fundList = [];
    let vipAccount;
    let routingRedirected = false;
    const applyProductTypeChange = async (selectedProductType) => {
      const redirected = await SubmitProductTypeChange(selectedProductType);
      routingRedirected = routingRedirected || redirected === true;
      return redirected;
    };
    if (session?.toggles?.productRoutingEnabled) {
      vipAccount = await vipApi.fetchData("/api/account");
      if (vipAccount) {
        setLimitAmount(Math.min(limitAmount, vipAccount.availableAmount));
        setPreferredCurrentLimit(vipAccount.limitAmount);
        setPreferredPreviousLimit(vipAccount.vippPreviousLimit);
      }
    }
    if (fundType === FUNDTYPE.ach) {
      fundList = await vipApi.fetchData("/api/bank/ach"); // ACH only
    } else {
      fundList = await vipApi.fetchData("/api/bank"); // Combined list
    }

    fundingSourceList.current = fundList;
    fundingSources.current = fundList;
    setDefaultFund(findDefaultBank(fundList));

    if (session?.toggles?.productRoutingEnabled) {
      const isVipOnlineLimitHigher =
        vipAccount?.onlineAccountsAvailLimit > vipAccount?.availableAmount;
      if (isOperatorBoth) {
        if (isVipOnlineLimitHigher && !noWarranty) {
          if (patronProductType === PRODUCTTYPES.PREFERRED) {
            // Mark the VIP Preferred Patron as VIP Online
            window.sessionStorage.setItem("onlyPlaid", true);
            await applyProductTypeChange(PRODUCTTYPES.ONLINE);
          } else {
            if (
              patronSelectedProductType === PRODUCTTYPES.PREFERRED &&
              patronProductType === PRODUCTTYPES.ONLINE
            ) {
              window.sessionStorage.setItem("onlyPlaid", true);
              await applyProductTypeChange(PRODUCTTYPES.ONLINE);
              //done
            }
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit,
            ); // Get only Plaid accounts
            if (fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit),
              );
            }

            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));
            setIsLoading(false);
          }
        } else {
          if (noWarranty) {
            const hasbanks = fundList.some(
              (fund) =>
                fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                fund.achOpenType === FUND_SOURCE_TYPE.FINICITY,
            );
            if (hasbanks) {
              setHasVerifiedBanks(hasbanks);
            }
            if (fundList && !hasbanks && unVerifiedBank) {
              faro.api.pushLog(
                ["Declined to add Verified bank, switching to VIPP"],
                { level: LogLevel.INFO },
              );
              await applyProductTypeChange(PRODUCTTYPES.PREFERRED); // 6
              return routingRedirected;
            } else {
              faro.api.pushLog(
                [
                  "Nowarranty Deposit, prompting patron to connect a verified bank",
                ],
                { level: LogLevel.INFO },
              );
              await applyProductTypeChange(PRODUCTTYPES.ONLINE); // 7
              return routingRedirected;
            }
          } else if (
            patronProductType === PRODUCTTYPES.ONLINE ||
            (patronSelectedProductType === PRODUCTTYPES.ONLINE &&
              patronProductType === PRODUCTTYPES.PREFERRED)
          ) {
            await applyProductTypeChange(PRODUCTTYPES.PREFERRED); //written down 8
          } else {
            setIsLoading(false);
          }
        }
      } else if (isOperatorOnline) {
        if (patronProductType === PRODUCTTYPES.PREFERRED) {
          // Mark the VIP Preferred Patron as VIP Online
          window.sessionStorage.setItem("onlyPlaid", true);
          await applyProductTypeChange(PRODUCTTYPES.ONLINE); //9
        } else {
          fundList = fundList.filter(
            (fund) =>
              (fund.achOpenType ===
                FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
              fund.availableLimit,
          ); // Get only Plaid accounts
          if (fundList.length > 0) {
            setLimitAmount(
              Math.min(operatorDailyLimit, fundList[0].availableLimit),
            );
          }

          fundingSources.current = fundList; // 10 - not called
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);
        }
      } else if (isOperatorPreferred) {
        if (patronProductType === PRODUCTTYPES.ONLINE) {
          await applyProductTypeChange(PRODUCTTYPES.PREFERRED); //11
        } else {
          setIsLoading(false);
        }
      }
    } else {
      if (
        patronProductType === patronSelectedProductType &&
        patronProductType === PRODUCTTYPES.PREFERRED
      ) {
        setIsLoading(false);
      } else if (
        patronProductType === patronSelectedProductType &&
        patronProductType === PRODUCTTYPES.ONLINE
      ) {
        fundList = fundList.filter(
          (fund) =>
            fund.achOpenType === FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
            fund.achOpenType === FUND_SOURCE_TYPE.FINICITY, //&& fund.availableLimit
        ); // Get only Plaid accounts

        if (fundList.length > 0) {
          setLimitAmount(
            Math.min(operatorDailyLimit, fundList[0].availableLimit),
          );
        }

        fundingSources.current = fundList;
        setDefaultFund(findDefaultBank(fundList));
        setIsLoading(false);
      } else {
        // Requirement document pt.5.c.i
        if (
          isOperatorBoth &&
          patronProductType === PRODUCTTYPES.PREFERRED &&
          patronSelectedProductType === PRODUCTTYPES.ONLINE
        ) {
          if (session.productTypeSwitched) {
            // Mark the VIP Preferred Patron as VIP Online
            window.sessionStorage.setItem("onlyPlaid", true);
            await applyProductTypeChange(PRODUCTTYPES.ONLINE); // 12
          } else {
            setIsLoading(false);
          }
        }

        // Requirement document pt.5.c.ii
        if (
          isOperatorBoth &&
          patronProductType === PRODUCTTYPES.ONLINE &&
          patronSelectedProductType === PRODUCTTYPES.PREFERRED
        ) {
          if (session.productTypeSwitched) {
            await applyProductTypeChange(PRODUCTTYPES.PREFERRED); //written down 13
          } else {
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit,
            ); // Get only Plaid accounts
            if (fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit),
              );
            }
            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));

            window.sessionStorage.setItem("onlyPlaid", true);
            setIsLoading(false);
          }
        }

        // Requirement document pt.5.c.iii
        if (
          !isOperatorOnline &&
          patronProductType === PRODUCTTYPES.ONLINE &&
          patronSelectedProductType === PRODUCTTYPES.PREFERRED
        ) {
          // Upgrade the VIP Online Patron to VIP Preferred without the pop-up.
          fundingSources.current = fundList;
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);
          await applyProductTypeChange(PRODUCTTYPES.PREFERRED); //written down 14
        }

        // Requirment document pt.5.c.iv
        if (
          !isOperatorPreferred &&
          patronProductType === PRODUCTTYPES.PREFERRED &&
          patronSelectedProductType === PRODUCTTYPES.ONLINE
        ) {
          // Mark the VIP Preferred Patron as VIP Online
          fundList = fundList.filter(
            (fund) =>
              (fund.achOpenType ===
                FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                fund.achOpenTypeP === FUND_SOURCE_TYPE.FINICITY) &&
              fund.availableLimit,
          ); // Get only Plaid accounts
          if (fundList.length > 0) {
            setLimitAmount(
              Math.min(operatorDailyLimit, fundList[0].availableLimit),
            );
          }
          fundingSources.current = fundList;
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);

          window.sessionStorage.setItem("onlyPlaid", true);
          await applyProductTypeChange(PRODUCTTYPES.ONLINE); // 15
        }
      }
    }

    if (!mounted) return routingRedirected;
    if (!fundList || fundList.length === 0) {
      history.push({ pathname: "/addAccount", state: { notRedirect: true } });
      return true;
    }
    return routingRedirected;
  }

  /**
   * Re-run the product routing decision after the funding source list has changed,
   * so session.patronProductType is current before the next screen renders.
   * determineProductSwitchAndLimits navigates on its own in some outcomes (no eligible
   * verified bank -> /addAccount, failed switch -> /error or /expired), so report whether
   * it did; callers must not push their own destination over one of those.
   * @returns {Promise<boolean>} true when routing already navigated.
   */
  async function refreshProductRouting() {
    if (!productRoutingEnabled) return false;
    return (await determineProductSwitchAndLimits()) === true;
  }

  return {
    onVipOnlineSubmit,
    onVipPreferredSubmit,
    showModals,
    patronProductType,
    patronSelectedProductType,
    isOperatorBoth,
    isOperatorPreferred,
    isOperatorOnline,
    productType,
    productRoutingEnabled,
    determineProductSwitchAndLimits,
    refreshProductRouting,
    apiReady: vipApi?.ready,
  };
}
